// Get the base URL and strip any trailing /api to avoid duplicates
const rawApiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
const cleanBaseUrl = rawApiUrl.replace(/\/api\/?$/, ''); // Strip trailing /api
const API_BASE_URL = cleanBaseUrl; // Don't add /api here, as all endpoint calls already include it
console.log('🔧 RAW API_URL:', rawApiUrl);
console.log('🔧 CLEANED API_BASE_URL:', API_BASE_URL);
console.log('🔧 REACT_APP_API_URL env var:', process.env.REACT_APP_API_URL);
console.log('🔧 NODE_ENV:', process.env.NODE_ENV);
console.log('🔧 All REACT_APP_ vars:', Object.keys(process.env).filter(key => key.startsWith('REACT_APP_')));

export interface CustomFieldDefinition {
  id: string;
  name: string;
  label: string;
  type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'url' | 'email' | 'phone' | 'person';
  options: string[];
  required: boolean;
  userId: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    fieldValues: number;
  };
}

export interface CustomFieldValue {
  id: string;
  founderId: string;
  fieldId: string;
  value: string | null;
  createdAt: string;
  updatedAt: string;
  field: CustomFieldDefinition;
  founder?: {
    id: string;
    name: string;
    profileUrl: string;
    company?: string;
  };
}

export interface Founder {
  id: string;
  name: string;
  profileUrl: string;
  roleTitle?: string;
  company?: string;
  stage?: string;
  industry?: string;
  location?: string;
  email?: string;
  followerCount?: number;
  lastPostExcerpt?: string;
  lastPostDate?: string;
  lastPostSummary?: string;  // AI-generated summary of latest post
  lastPostSummaryDate?: string; // When the summary was generated
  lastTriggers: string[];
  priorityScore?: number;
  nextAction?: string;
  nextActionExpiresAt?: string;
  status: string;
  tag?: string;
  owner?: string;
  lastTouchAt?: string;
  snoozedUntil?: string;
  tabId?: string;
  createdAt: string;
  updatedAt: string;
  customFieldValues?: CustomFieldValue[];
}

export interface Post {
  id: string;
  founderId: string;
  postUrl: string;
  postTimestamp: string;
  type?: string;
  content: string;
  likes: number;
  comments: number;
  reposts: number;
  triggers: string[];
  engagementRate?: number;
  parsedCompany?: string;
  parsedInvestors?: string;
  parsedAmount?: string;
  createdAt: string;
  founder?: Founder;
}

export interface Activity {
  id: string;
  founderId: string;
  ts: string;
  channel: string;
  templateUsed?: string;
  notes?: string;
  result?: string;
  nextFollowupAt?: string;
  founder?: Founder;
}

export interface PostSummary {
  id: string;
  founderId: string;
  postId: string;
  summary: string;
  summaryDate: string;
  postDate: string;
  post?: {
    postUrl: string;
    likes: number;
    comments: number;
    reposts: number;
  };
}

export interface FounderProfile {
  founder: Founder;
  timeline: PostSummary[];
  recentPosts: Post[];
  recentActivities: Activity[];
}

class ApiService {
  private async request<T>(endpoint: string, options?: RequestInit): Promise<T> {
    const url = `${API_BASE_URL}${endpoint}`;

    // Enhanced logging for debugging
    if (endpoint.includes('/auth/')) {
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
      console.log('🌐 API REQUEST:', new Date().toISOString());
      console.log('  Endpoint:', endpoint);
      console.log('  Full URL:', url);
      console.log('  Method:', options?.method || 'GET');
      console.log('  Has body:', !!options?.body);
      console.log('  Using cookie auth: true');
      console.log('  Credentials:', options?.credentials ?? 'include');
      if (options?.body) {
        try {
          const bodyObj = JSON.parse(options.body as string);
          const sanitized = { ...bodyObj };
          if (sanitized.password) sanitized.password = '***';
          console.log('  Body:', JSON.stringify(sanitized, null, 2));
        } catch (e) {
          console.log('  Body (raw):', options.body);
        }
      }
      console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
    }

    try {
      const fetchStartTime = Date.now();
      console.log('📤 Fetch starting...');

      const response = await fetch(url, {
        // Always include credentials so HTTP-only auth cookies are sent
        credentials: options?.credentials ?? 'include',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.headers as Record<string, string>),
        },
        ...options,
      });
      
      const fetchDuration = Date.now() - fetchStartTime;
      
      if (endpoint.includes('/auth/')) {
        console.log('📥 Fetch response received:', {
          status: response.status,
          statusText: response.statusText,
          ok: response.ok,
          duration: `${fetchDuration}ms`,
          headers: Object.fromEntries(response.headers.entries())
        });
      }

      if (!response.ok) {
        let errorMessage = `API request failed: ${response.status} ${response.statusText}`;
        let errorData = null;
        try {
          errorData = await response.json();
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (e) {
          // Use default error message
          console.log('⚠️ Could not parse error response as JSON');
        }
        
        if (endpoint.includes('/auth/')) {
          console.error('❌ API request failed:', {
            status: response.status,
            statusText: response.statusText,
            errorMessage,
            errorData,
            url
          });
        }
        
        const error = new Error(errorMessage) as any;
        error.status = response.status;
        throw error;
      }

      // Handle 204 No Content responses (like DELETE requests)
      if (response.status === 204) {
        return null as any;
      }

      // Handle 304 Not Modified - return cached data or empty response
      if (response.status === 304) {
        // 304 has no body, so we can't parse it
        // The browser should use cached data
        return {} as T;
      }

      return response.json();
    } catch (error: any) {
      // Handle network errors (server not running, connection refused, etc.)
      if (endpoint.includes('/auth/')) {
        console.error('❌ API request error:', {
          name: error.name,
          message: error.message,
          stack: error.stack,
          url
        });
      }
      
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        console.error('❌ Network error - fetch failed:', {
          message: error.message,
          url
        });
        const networkError = new Error('Failed to fetch') as any;
        networkError.name = 'NetworkError';
        networkError.status = 0;
        networkError.originalError = error;
        throw networkError;
      }
      throw error;
    }
  }

  private buildQueryString(params: Record<string, any>): string {
    const searchParams = new URLSearchParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });
    const query = searchParams.toString();
    return query ? `?${query}` : '';
  }

  // Generic CRUD operations
  private async get<T>(endpoint: string, params?: Record<string, any>): Promise<T> {
    const query = this.buildQueryString(params || {});
    return this.request(`${endpoint}${query}`);
  }

  private async post<T>(endpoint: string, data?: any): Promise<T> {
    return this.request(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  private async put<T>(endpoint: string, data?: any): Promise<T> {
    return this.request(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  private async delete<T>(endpoint: string): Promise<T> {
    return this.request(endpoint, { method: 'DELETE' });
  }

  private async patch<T>(endpoint: string, data?: any): Promise<T> {
    return this.request(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // Founders
  async getFounders(limit?: number, page?: number) {
    return this.get<{ data: Founder[]; pagination: { total: number; page: number; limit: number; pages: number } }>('/api/founders', { limit, page });
  }

  async getFounder(id: string) {
    return this.get<Founder>(`/api/founders/${id}`);
  }

  async getFounderProfile(id: string) {
    return this.get<FounderProfile>(`/api/founders/${id}/profile`);
  }

  async getFounderSummaries(id: string, days: number = 30) {
    return this.get<{ timeline: PostSummary[]; count: number }>(`/api/founders/${id}/summaries`, { days });
  }

  async createFoundername(founder: Partial<Founder>) {
    return this.post<Founder>('/api/founders', founder);
  }

  async updateFounder(id: string, updates: Partial<Founder>) {
    return this.put<Founder>(`/api/founders/${id}`, updates);
  }

  async deleteFounder(id: string) {
    return this.delete<void>(`/api/founders/${id}`);
  }

  async updateFounderSummary(id: string) {
    return this.post<{ success: boolean; message: string }>(`/api/founders/${id}/update-summary`);
  }

  async updateAllFounderSummaries() {
    return this.post<{ success: boolean; message: string }>('/api/founders/update-all-summaries');
  }

  async bulkUpdateFounders(founderIds: string[], updates: Partial<Founder>) {
    return this.patch<{ success: boolean; count: number; message: string }>('/api/founders/bulk-update', {
      founderIds,
      updates
    });
  }

  // Posts
  async getPosts(limit?: number, offset?: number) {
    return this.get<{ data: Post[]; total: number }>('/api/posts', { limit, offset });
  }

  async getPost(id: string) {
    return this.get<Post>(`/api/posts/${id}`);
  }

  async getPostsByFounder(founderId: string, limit?: number) {
    return this.get<{ data: Post[]; total: number }>(`/api/posts/founder/${founderId}`, { limit });
  }

  // Activities
  async getActivities(limit?: number, offset?: number) {
    return this.get<{ data: Activity[]; total: number }>('/api/activities', { limit, offset });
  }

  async getActivitiesByFounder(founderId: string, limit?: number) {
    return this.get<{ data: Activity[]; total: number }>(`/api/activities/founder/${founderId}`, { limit });
  }

  // AI Analysis
  async analyzePost(postId: string) {
    return this.post<any>('/api/ai/analyze/post', { postId });
  }

  async analyzeFounder(founderId: string) {
    return this.post<any>('/api/ai/analyze/founder', { founderId });
  }

  async generateEmail(founderId: string, template: string = 'intro') {
    return this.post<any>('/api/ai/generate-email', { founderId, template });
  }

  async getOutreachOpportunities(limit?: number) {
    return this.get<any>('/api/ai/outreach/opportunities', { limit });
  }

  async getWeeklyUpdates(weeks: number = 8) {
    return this.get<{ data: any[] }>('/api/tasks/weekly-updates', { weeks, t: Date.now() });
  }

  // Authentication
  async login(email: string, password: string) {
    return this.post<{ user: any; token: string; message: string }>('/api/auth/login', { email, password });
  }

  async register(email: string, password: string, name?: string) {
    return this.post<{ user: any; token: string; message: string }>('/api/auth/register', { email, password, name });
  }

  async getCurrentUser() {
    return this.get<any>('/api/auth/me');
  }

  async logout() {
    return this.post<{ message: string }>('/api/auth/logout', {});
  }

  async completeOnboarding(onboardingData: {
    company?: string;
    phone: string;
    timezone: string;
    dailySummaryEnabled: boolean;
  }) {
    return this.post<any>('/api/auth/complete-onboarding', onboardingData);
  }

  async completeWalkthrough() {
    return this.request<any>('/api/auth/walkthrough-complete', {
      method: 'PATCH',
    });
  }

  async updateUserProfile(profileData: {
    name: string; email: string; company: string; jobTitle: string;
    industry: string; primaryUseCase: string; goals: string;
    phone: string; linkedinUrl: string; notes?: string;
    dailySummaryEnabled?: boolean;
  }) {
    return this.put<any>('/api/auth/update-profile', profileData);
  }

  // Custom Fields
  async getCustomFields() {
    return this.get<CustomFieldDefinition[]>('/api/custom-fields');
  }

  async createCustomField(fieldData: {
    name: string; label: string;
    type: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean' | 'url' | 'email' | 'phone' | 'person';
    options?: string[]; required?: boolean;
  }) {
    return this.post<CustomFieldDefinition>('/api/custom-fields', fieldData);
  }

  async updateCustomField(id: string, fieldData: {
    label?: string;
    type?: 'text' | 'number' | 'date' | 'select' | 'multiselect' | 'boolean';
    options?: string[]; required?: boolean;
  }) {
    return this.put<CustomFieldDefinition>(`/api/custom-fields/${id}`, fieldData);
  }

  async deleteCustomField(id: string) {
    return this.delete<void>(`/api/custom-fields/${id}`);
  }

  async setCustomFieldValue(fieldId: string, founderId: string, value: string | string[]) {
    return this.post<CustomFieldValue>(`/api/custom-fields/${fieldId}/values`, { founderId, value });
  }

  async updateCustomFieldValue(fieldId: string, founderId: string, value: string | string[]) {
    return this.put<CustomFieldValue>(`/api/custom-fields/${fieldId}/values/${founderId}`, { value });
  }

  async deleteCustomFieldValue(fieldId: string, founderId: string) {
    return this.delete<void>(`/api/custom-fields/${fieldId}/values/${founderId}`);
  }

  // AI Summary
  async getLatestAISummary(founderId: string) {
    return this.get<{ founderId: string; profileUrl: string; latestAISummary: string | null; latestPostTimestamp: string | null; lastUpdatedAt: string | null }>(`/api/founders/${founderId}/latest-ai-summary`);
  }

  async refreshAISummaries(founderId: string) {
    return this.post<{ success: boolean; message: string; newPostsCount: number; updatedSummariesCount: number; error?: string }>(`/api/founders/${founderId}/refresh-summaries`);
  }

  async refreshAllProfiles() {
    return this.post<{ 
      success: boolean; 
      message: string; 
      totalProfiles: number;
      queuedJobs: number;
      jobs: Array<{
        founderId: string;
        founderName: string;
        profileUrl: string;
        jobId: number;
      }>;
    }>('/api/founders/refresh-all');
  }

  async getRefreshStatus() {
    return this.get<{
      success: boolean;
      totalProfiles: number;
      profilesWithLinkedIn: number;
      lastRefreshTimes: Array<{
        founderName: string;
        profileUrl: string;
        lastUpdatedAt: string | null;
      }>;
    }>('/api/founders/refresh-status');
  }

  async getJobQueueStatus() {
    const response = await this.get<{
      success: boolean;
      pending: number;
      running: number;
      completed: number;
      error: number;
      total: number;
      estimatedWaitTimeSeconds: number;
      estimatedWaitTimeMinutes: number;
      estimatedWaitTimeText: string;
      currentRunningJobUserId: string | null;
      currentRunningJobUserEmail: string | null;
    }>('/api/founders/phantom-jobs/status');
    
    // Return just the status data without the success wrapper
    return {
      pending: response.pending,
      running: response.running,
      completed: response.completed,
      error: response.error,
      total: response.total,
      estimatedWaitTimeSeconds: response.estimatedWaitTimeSeconds,
      estimatedWaitTimeMinutes: response.estimatedWaitTimeMinutes,
      estimatedWaitTimeText: response.estimatedWaitTimeText,
      currentRunningJobUserId: response.currentRunningJobUserId,
      currentRunningJobUserEmail: response.currentRunningJobUserEmail
    };
  }

  async getLinkedinPosts(founderId: string) {
    const response = await this.get<{
      success: boolean;
      posts: Array<{
        id: string;
        postUrl: string;
        postContent: string;
        postTimestamp: string;
        profileUrl: string;
      }>;
    }>(`/api/founders/${founderId}/linkedin-posts`);
    
    return response.posts || [];
  }

  async getFounderAITimeline(founderId: string, limit?: number) {
    const response = await this.get<{
      success: boolean;
      posts: Array<{
        id: string;
        postUrl: string;
        postContent: string;
        postTimestamp: string;
        aiSummary: string;
        engagement: {
          likes: number;
          comments: number;
          reposts: number;
          views: number;
        };
        media: {
          type: string | null;
          imageUrl: string | null;
          videoUrl: string | null;
        };
        createdAt: string;
        updatedAt: string;
      }>;
      founder: {
        id: string;
        name: string;
        profileUrl: string;
      };
      total: number;
      message?: string;
    }>(`/api/founders/${founderId}/ai-timeline`, { limit });
    
    return response;
  }

  async getPhantomJobStatus() {
    const response = await this.get<{
      hasActivePhantomJobs: boolean;
      message: string;
    }>('/api/founders/phantom-status');
    
    return response;
  }

  // Stripe / Subscription methods
  async createCheckoutSession(priceId: string, plan: string) {
    return this.post<{ sessionId: string; url: string }>('/api/stripe/create-checkout-session', {
      priceId,
      plan,
    });
  }

  async createPortalSession() {
    return this.post<{ url: string }>('/api/stripe/create-portal-session', {});
  }

  async getSubscription() {
    return this.get<{
      status: string;
      plan: string;
      trialEndsAt?: string;
      subscriptionEndsAt?: string;
      isCanceled: boolean;
      hasStripeCustomer: boolean;
    }>('/api/stripe/subscription');
  }

  // LinkedIn Connections methods
  async exportLinkedInConnections(linkedinProfileUrl: string) {
    return this.post<{ success: boolean; containerId: string; message: string }>(
      '/api/linkedin-connections/export',
      {
        linkedinProfileUrl,
      }
    );
  }

  async checkLinkedInExportStatus(containerId: string) {
    return this.get<{
      status: 'running' | 'finished' | 'error';
      connections?: Array<{
        fullName: string;
        company?: string;
        title?: string;
        profileUrl: string;
        location?: string;
        email?: string;
      }>;
      message?: string;
      error?: string;
    }>(`/api/linkedin-connections/status/${containerId}`);
  }

  async importLinkedInConnections(connections: Array<{
    fullName: string;
    company?: string;
    title?: string;
    profileUrl: string;
    location?: string;
    email?: string;
  }>) {
    return this.post<{
      success: boolean;
      imported: number;
      skipped: number;
      importedIds: string[];
    }>('/api/linkedin-connections/import', { connections });
  }

  async startLinkedInLogin() {
    return this.post<{
      success: boolean;
      sessionId: string;
      browserUrl?: string;
      message: string;
    }>('/api/linkedin-connections/login', {});
  }

  async checkLinkedInLoginStatus(sessionId: string) {
    return this.get<{
      success: boolean;
      status: 'pending' | 'waiting' | 'completed' | 'error' | 'timeout';
      cookie: string | null;
      error: string | null;
    }>(`/api/linkedin-connections/login/status/${sessionId}`);
  }

  async completeLinkedInLogin(sessionId: string) {
    return this.post<{
      success: boolean;
      containerId: string;
      message: string;
    }>('/api/linkedin-connections/login/complete', { sessionId });
  }

  async cancelLinkedInLogin(sessionId: string) {
    return this.post<{
      success: boolean;
      message: string;
    }>('/api/linkedin-connections/login/cancel', { sessionId });
  }

  async saveLinkedInCookie(liAtCookie: string) {
    return this.post<{
      success: boolean;
      message: string;
    }>('/api/auth/linkedin/save-cookie', { liAtCookie });
  }

  async submitBugReport(data: {
    type: 'bug' | 'feature';
    contact: string; // email or phone number
    description: string;
    screenshot: string | null;
  }) {
    return this.post<{
      success: boolean;
      message: string;
      cardId?: string;
    }>('/api/bug-reports', data);
  }

  // Outlook integration methods
  async getOutlookStatus() {
    return this.get<{
      connected: boolean;
      needsReconnect?: boolean;
      profile?: {
        email: string;
        displayName: string;
      };
      message?: string;
    }>('/api/outlook/status');
  }

  async sendOutlookEmail(data: {
    to: string | string[];
    subject: string;
    body: string;
    cc?: string | string[];
    bcc?: string | string[];
  }) {
    return this.post<{
      success: boolean;
      message: string;
    }>('/api/outlook/send', data);
  }

  async disconnectOutlook() {
    return this.post<{
      success: boolean;
      message: string;
    }>('/api/outlook/disconnect', {});
  }

  // Gmail integration methods
  async getGmailStatus() {
    return this.get<{
      connected: boolean;
      needsReconnect?: boolean;
      profile?: {
        email: string;
      };
      message?: string;
    }>('/api/gmail/status');
  }

  async disconnectGmail() {
    return this.post<{
      success: boolean;
      message: string;
    }>('/api/gmail/disconnect', {});
  }

  async getGmailEmails(params?: {
    query?: string;
    label?: 'INBOX' | 'SENT' | 'STARRED' | 'IMPORTANT' | 'UNREAD';
    limit?: number;
  }) {
    const searchParams = new URLSearchParams();
    if (params?.query) searchParams.append('query', params.query);
    if (params?.label) searchParams.append('label', params.label);
    if (params?.limit) searchParams.append('limit', params.limit.toString());

    const queryString = searchParams.toString();
    return this.get<{
      success: boolean;
      emails: Array<{
        id: string;
        threadId: string;
        subject: string;
        from: string;
        to: string;
        date: string;
        snippet: string;
        labels: string[];
      }>;
      count: number;
    }>(`/api/gmail/emails${queryString ? `?${queryString}` : ''}`);
  }

  // Notion Integration methods
  async getNotionStatus() {
    return this.get<{
      connected: boolean;
      pageId: string | null;
      lastSync: string | null;
      hasMapping?: boolean;
      importedFoundersCount?: number;
    }>('/api/notion/status');
  }

  async connectNotionPage(pageUrl: string) {
    return this.post<{
      success: boolean;
      pageId: string;
      message: string;
      mapping?: any;
      pendingConfirmation?: boolean;
      entryCount?: number;
      notionColumns?: string[];
    }>('/api/notion/connect-page', { pageUrl });
  }

  async readNotionPage() {
    return this.get<{
      success: boolean;
      content: string;
    }>('/api/notion/read');
  }

  async disconnectNotion() {
    return this.post<{
      success: boolean;
      message: string;
    }>('/api/notion/disconnect', {});
  }

  async confirmNotionMapping(mapping: any) {
    return this.post<{
      success: boolean;
      message: string;
      entryCount: number;
    }>('/api/notion/confirm-mapping', { mapping });
  }

  async getNotionFieldMapping() {
    return this.get<{
      mapping: any | null;
    }>('/api/notion/mapping');
  }

  async updateNotionMapping(mapping: any) {
    return this.put<{
      success: boolean;
      message: string;
      mapping: any;
    }>('/api/notion/mapping', { mapping });
  }

  async triggerNotionSync(forceFull: boolean = false) {
    const url = forceFull ? '/api/notion/sync-now?full=true' : '/api/notion/sync-now';
    return this.post<{
      success: boolean;
      message: string;
      result: {
        synced: number;
        created: number;
        updated: number;
        errors: number;
      };
    }>(url, {});
  }

  async triggerNotionImport() {
    return this.post<{
      success: boolean;
      message: string;
      entryCount: number;
    }>('/api/notion/import-now', {});
  }

  async getRecentOutlookEmails() {
    return this.get<{
      success: boolean;
      emails: Array<{
        subject: string;
        body: string;
        bodyPreview: string;
        to: string;
        sentDateTime: string;
      }>;
      count: number;
      connected?: boolean;
      error?: string;
    }>('/api/outlook/emails/recent');
  }
}

export const apiService = new ApiService();
export default apiService;
